import type { ExpressionSpecification } from "maplibre-gl";

import type { DeforestationLayerStates } from "../types/deforestation";

export type ContextFillLayerId =
  | "context-capad-fill"
  | "context-ramsar-fill"
  | "context-world-heritage-fill"
  | "context-national-heritage-fill"
  | "context-commonwealth-heritage-fill"
  | "context-rfa-fill"
  | "context-regulated-veg-fill"
  | "context-bushfire-history-fill"
  | "context-koala-habitat-fill"
  | "context-critical-habitat-fill";

export interface ContextLayerMeta {
  // Maplibre fill-layer id, used for click queries and color sync.
  fillLayerId: ContextFillLayerId;
  // Display label shown in the popup chip + detail header.
  label: string;
  // DeforestationLayerStates key — used to read the user-chosen color.
  stateKey: keyof DeforestationLayerStates;
  // Human label for the feature title — picks the first non-empty prop.
  titleProps: string[];
  // Properties to render as `key: value` rows in the detail table (in order).
  detailProps: { key: string; label: string }[];
  // Optional per-prop lookup that maps a raw attribute value to a display
  // string — e.g. QLD RVM category codes ("A") → a descriptive label. Used
  // by both the title and the detail rows.
  valueMaps?: Partial<Record<string, Record<string, string>>>;
}

// QLD VM Act regulated vegetation category descriptors, worded to match the
// Queensland Government Regulated Vegetation Management Map legend. Category X
// is deliberately left as a plain "Category X" (no descriptor) — wording that
// implies X is freely clearable is misleading. The letter prefix
// is preserved so colour-keying that reads the first token keeps working.
export const QLD_RVM_CATEGORY_LABELS: Record<string, string> = {
  A: "A — Vegetation offsets, compliance notices and VDecs",
  B: "B — Remnant vegetation",
  C: "C — High-value regrowth vegetation",
  R: "R — Reef regrowth watercourse vegetation",
  X: "X — Category X",
  Water: "Water — Watercourse buffer",
};

// Per-category fill colour for the QLD regulated-vegetation layer. Regulated
// tiers (A/B/C/R) stay green-ish, X is amber, Water blue. Kept in this file
// so the map renderer and the parcel-detail panel pull from one palette.
export const QLD_RVM_CATEGORY_COLORS: Record<string, string> = {
  A: "#84cc16",
  B: "#15803d",
  C: "#22c55e",
  R: "#86efac",
  X: "#f59e0b",
  Water: "#3b82f6",
};

export const QLD_RVM_CATEGORY_FALLBACK_COLOR = "#a1a1aa";

// MapLibre `match` expression that colours a feature by its rvm_cat /
// RVM_CAT attribute. Used as the `fill-color` / `line-color` paint property
// on the regulated-veg layers so each category gets its own colour rather
// than a single flat fill.
export function qldRvmCategoryColorExpression(): ExpressionSpecification {
  const stops: (string | ExpressionSpecification)[] = [];
  for (const [cat, color] of Object.entries(QLD_RVM_CATEGORY_COLORS)) {
    stops.push(cat, color);
  }
  return [
    "match",
    ["coalesce", ["get", "rvm_cat"], ["get", "RVM_CAT"]],
    ...stops,
    QLD_RVM_CATEGORY_FALLBACK_COLOR,
  ] as unknown as ExpressionSpecification;
}

export const CONTEXT_LAYER_META: Record<ContextFillLayerId, ContextLayerMeta> = {
  "context-capad-fill": {
    fillLayerId: "context-capad-fill",
    label: "Protected Area",
    stateKey: "protectedMatters",
    titleProps: ["NAME", "name"],
    detailProps: [
      { key: "TYPE", label: "Type" },
      { key: "TYPE_ABBR", label: "Type (abbr)" },
      { key: "IUCN", label: "IUCN category" },
      { key: "STATE", label: "State" },
      { key: "AUTHORITY", label: "Authority" },
      { key: "GOVERNANCE", label: "Governance" },
      { key: "NRS_PA", label: "NRS protected area" },
      { key: "GAZ_DATE", label: "Gazetted" },
      { key: "LATEST_GAZ", label: "Latest gazettal" },
      { key: "GAZ_AREA", label: "Gazetted area (ha)" },
      { key: "GIS_AREA", label: "GIS area (ha)" },
      { key: "MGT_PLAN", label: "Mgmt plan" },
      { key: "EPBC", label: "EPBC" },
      { key: "PA_SYSTEM", label: "PA system" },
      { key: "PA_ID", label: "Protected area ID" },
    ],
  },
  "context-ramsar-fill": {
    fillLayerId: "context-ramsar-fill",
    label: "Ramsar Wetland",
    stateKey: "ramsarWetlands",
    titleProps: ["RAMSAR_NAM", "NAME", "name"],
    detailProps: [
      { key: "WETLAND_NA", label: "Wetland" },
      { key: "STATE", label: "State" },
      { key: "JURISDICTI", label: "Jurisdiction" },
      { key: "DESIGNATIO", label: "Designated" },
      { key: "GAZETTED_A", label: "Gazettal area (ha)" },
      { key: "AREA_HA", label: "Area (ha)" },
      { key: "TOTAL_SITE", label: "Total site (ha)" },
      { key: "REFCODE", label: "Reference code" },
      { key: "WETLANDSDB", label: "Wetlands DB" },
      { key: "METADATA_U", label: "Metadata URL" },
      { key: "SOURCE", label: "Source" },
    ],
  },
  "context-world-heritage-fill": {
    fillLayerId: "context-world-heritage-fill",
    label: "World Heritage",
    stateKey: "worldHeritage",
    titleProps: ["NAME", "name"],
    detailProps: [
      { key: "STATUS", label: "Status" },
      { key: "INSCRIBED", label: "Inscribed" },
      { key: "STATE", label: "State" },
      { key: "ADDRESS", label: "Address" },
      { key: "IUCN_MGT", label: "IUCN management" },
      { key: "BIOGEOPROV", label: "Biogeographic province" },
      { key: "NATURAL", label: "Natural criteria" },
      { key: "CULTURAL", label: "Cultural criteria" },
      { key: "HECTARES", label: "Area (ha)" },
      { key: "PLACE_ID", label: "Place ID" },
    ],
  },
  "context-national-heritage-fill": {
    fillLayerId: "context-national-heritage-fill",
    label: "National Heritage",
    stateKey: "nationalHeritage",
    titleProps: ["NAME", "name"],
    detailProps: [
      { key: "CLASS", label: "Class" },
      { key: "STATUS", label: "Status" },
      { key: "STATE", label: "State" },
      { key: "ADDRESS", label: "Address" },
      { key: "REGISTER_D", label: "Registered" },
      { key: "UPDATED", label: "Updated" },
      { key: "URL", label: "DCCEEW page" },
      { key: "AREA_HA", label: "Area (ha)" },
      { key: "PLACE_ID", label: "Place ID" },
    ],
  },
  "context-commonwealth-heritage-fill": {
    fillLayerId: "context-commonwealth-heritage-fill",
    label: "Commonwealth Heritage",
    stateKey: "commonwealthHeritage",
    titleProps: ["NAME", "name"],
    detailProps: [
      { key: "CLASS", label: "Class" },
      { key: "STATUS", label: "Status" },
      { key: "STATE", label: "State" },
      { key: "ADDRESS", label: "Address" },
      { key: "REGISTER", label: "Registered" },
      { key: "UPDATE_", label: "Updated" },
      { key: "AREA_HA", label: "Area (ha)" },
      { key: "PLACE_ID", label: "Place ID" },
    ],
  },
  "context-rfa-fill": {
    fillLayerId: "context-rfa-fill",
    label: "Regional Forest Agreement",
    stateKey: "regionalForestAgreements",
    titleProps: ["REG_NAM", "RFA_NAME", "NAME", "name"],
    detailProps: [
      { key: "REG_TYPE", label: "Boundary type" },
      { key: "STATE", label: "State" },
    ],
  },
  "context-regulated-veg-fill": {
    fillLayerId: "context-regulated-veg-fill",
    label: "Regulated Vegetation",
    stateKey: "regulatedVeg",
    titleProps: ["rvm_cat", "RVM_CAT"],
    detailProps: [
      { key: "rvm_cat", label: "Category" },
      { key: "version", label: "Version" },
    ],
    valueMaps: {
      rvm_cat: QLD_RVM_CATEGORY_LABELS,
      RVM_CAT: QLD_RVM_CATEGORY_LABELS,
    },
  },
  "context-bushfire-history-fill": {
    fillLayerId: "context-bushfire-history-fill",
    label: "Bushfire",
    stateKey: "bushfireHistory",
    // PoC-2 clean South-Burnett layer carries fire_type / ignition_year.
    titleProps: ["fire_type"],
    detailProps: [
      { key: "fire_type", label: "Type" },
      { key: "ignition_year", label: "Ignition year" },
    ],
  },
  "context-koala-habitat-fill": {
    fillLayerId: "context-koala-habitat-fill",
    label: "Koala Habitat",
    stateKey: "koalaHabitat",
    titleProps: ["GRIDCODE"],
    detailProps: [{ key: "GRIDCODE", label: "Grid code" }],
  },
  "context-critical-habitat-fill": {
    fillLayerId: "context-critical-habitat-fill",
    label: "Critical Habitat",
    stateKey: "listedCriticalHabitat",
    titleProps: ["critical_habitat"],
    detailProps: [
      { key: "critical_habitat", label: "Listed habitat" },
      { key: "state", label: "State" },
    ],
  },
};

export const CONTEXT_FILL_LAYER_IDS = Object.keys(
  CONTEXT_LAYER_META,
) as ContextFillLayerId[];

// Per-feature swatch colour. Falls back to the flat layer colour from the
// left-panel state, but for layers with a categorical paint expression
// (e.g. regulated vegetation) returns the category-specific colour so the
// popup matches what's drawn on the map.
export function pickFeatureColor(
  feature: GeoJSON.Feature,
  fillLayerId: ContextFillLayerId,
  fallback: string,
): string {
  if (fillLayerId === "context-regulated-veg-fill") {
    const props = (feature.properties ?? {}) as Record<string, unknown>;
    const cat = props.rvm_cat ?? props.RVM_CAT;
    if (cat != null) {
      const hex = QLD_RVM_CATEGORY_COLORS[String(cat)];
      if (hex) return hex;
    }
    return QLD_RVM_CATEGORY_FALLBACK_COLOR;
  }
  return fallback;
}

export function pickTitle(
  feature: GeoJSON.Feature,
  meta: ContextLayerMeta,
): string {
  const props = (feature.properties ?? {}) as Record<string, unknown>;
  for (const k of meta.titleProps) {
    const v = props[k];
    if (v !== undefined && v !== null && String(v).length > 0) {
      // Prefer a mapped display label (e.g. RVM category code → descriptor).
      const mapped = meta.valueMaps?.[k]?.[String(v)];
      if (mapped) return mapped;
      const s = String(v);
      // Bushfires often store ALL CAPS — title-case for display.
      return s.length <= 4
        ? s
        : s
            .toLowerCase()
            .replace(/\b\w/g, (c) => c.toUpperCase())
            .replace(/_/g, " ");
    }
  }
  return meta.label;
}

export function formatPropValue(v: unknown): string {
  if (v === null || v === undefined) return "—";
  const s = String(v);
  if (s.length === 0) return "—";
  // ISO date → YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}T/.test(s)) return s.slice(0, 10);
  // Numeric → 2 dp if it looks like a float, integer otherwise
  if (typeof v === "number") {
    return Number.isInteger(v) ? v.toString() : v.toFixed(1);
  }
  return s;
}
